
interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface ingredients {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
    items: any;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    // ingredients: ingredients;
    // treatmentAreas1: TreatmentArea[];
    // treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const obesity: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} =
{
    "en": {
        "url": "obesity-profile",
        "meta": {
            "title": "Obesity Profile Diagnostic Assessment | Elite Body Home",
            "description": "Comprehensive obesity profile evaluating health indicators affecting body weight, metabolism and overall health to create personalized treatment plans.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/obesity-profile/",
            "og": {
                "title": "Obesity Profile Diagnostic Assessment | Elite Body Home",
                "description": "Thorough evaluation of metabolic health and obesity-related risk factors",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-53.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Obesity Profile"],
        "hero": {
            "title": "Obesity Profile Diagnostic Assessment",
            "subtitle": "Understanding the Root Causes of Weight Gain",
            "description": "Obesity is a major health issue impacting millions of people all over the world, causing numerous physical and metabolic disorders. It is important that we consider the factors leading to individuals' weight gain before helping them achieve their health goals. An Obesity Profile is a specific diagnostic assessment that evaluates different health indicators that can affect body weight, metabolism, and general health."
        },
        "howItWorks": {
            "title": "What's in Our Obesity Profile",
            "description": "A multitude of factors can impact metabolism, so a thorough evaluation should aid in diagnosing potential metabolic disorders, nutritional deficiencies, and obesity-related risk factors, allowing healthcare providers to craft individualized modes of treatment.",
            "items": [
                "GFFBS – Glucose – Fasting (FBS), 82947",
                "PPBS – Glucose – Post Prandial (PPBS), 82947",
                "CBC – Complete Blood Count (CBC), 85025",
                "HBA1C1 – Hemoglobin A1c, 83036",
                "LPPROF – Lipid Profile, 0",
                "CPK – Creatine Kinase (CPK), 82550",
                "UREA – Urea, 84520",
                "URAC – Uric Acid, 84550",
                "CREAT – Creatinine, 82565",
                "SOD – Sodium, 84295",
                "PM – Potassium, 84132",
                "CHLO – Chloride, 82435",
                "PROT — Protein — Total, 84155",
                "ALBU – Albumin, 82040",
                "GLB – Globulin, NA",
                "BILT – Bilirubin, Total, 82247",
                "BILD – Bilirubin Direct (Conjugated), 82248",
                "CAL – Calcium, 82310",
                "PS – Phosphate, 84100",
                "ALT – ALT/SGPT (Alanine Aminotransferase), 84460",
                "AST – AST/SGOT (Aspartate Aminotransferase), 84450",
                "GGT – Gamma Glutamyl Transferase (GGT), 82977",
                "TSH1 – Thyroid Stimulating Hormone (TSH), 84443",
                "FT3 – FT3 (Free Tri-Iodothyronine), 84481",
                "FT4 – FT4 (Thyroxine – Free), 84439",
                "RFQN – Rheumatoid Factor (RF) Quantitative, 86431",
                "ISR – Insulin – Random, 83525",
                "CORAM – Cortisol – AM, 82533",
                "CPEP – C-Peptide – Random, 84681"
            ]
        },
        "procedureSteps": {
            "heading": "Procedure",
            "para": "Obesity Profile: This is a battery of laboratory examinations and clinical evaluations which reflect the obesity subject's general condition and risk factors. These include hormone testing, metabolic testing, and other tests that can reveal underlying issues that may be contributing to weight gain or weight loss resistance.",
            "steps": [
                {
                    "title": "Initial Consultation",
                    "desc": "An extensive discussion with a physician about your medical history, lifestyle, and weight-related concerns to customize the profile.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Body Composition Analysis",
                    "desc": "Get a complete picture of your weight composition. Assess values such as your body mass index (BMI), fat, muscle mass, and other indicators to understand how you are building weight.",
                    "icon": "fa-solid fa-weight"
                },
                {
                    "title": "Laboratory Tests",
                    "desc": "Blood tests to measure cholesterol, blood sugar, thyroid function and other obesity-related markers.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "Metabolic Assessment",
                    "desc": "Identify your metabolic rate and energy expenditure to know how well your body burns calories.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Nutritional Assessment",
                    "desc": "Sit down with a nutritionist to study your dietary intake and lifestyle and find gaps or patterns of poor eating.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Physical Activity Evaluation",
                    "desc": "Consider your exercise routine and how often you are active; you must balance your exercise with what you eat for it's impact on your weight.",
                    "icon": "fa-solid fa-running"
                },
                {
                    "title": "Doctor's Review and Recommendations",
                    "desc": "After all data is collected, a physician will review the results and recommend a personalized weight management program, including diet, exercise and lifestyle changes.",
                    "icon": "fa-solid fa-user-md"
                },
                {
                    "title": "Follow-Up Plan",
                    "desc": "Long-term follow-up sessions to track progress and revision of the plan as necessary.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "Putting together a specific Obesity Profile — a very thorough analysis to figure out what exactly your health needs are — can bring people who are having issues with the current system of weight management a lot of benefits.",
            "items": [
                {
                    "title": "Personalized Treatment Plans",
                    "desc": "Understanding why a person has become obese or overweight is a prerequisite for devising a personalised weight loss plan.",
                    "icon": "fa-solid fa-clipboard-list"
                },
                {
                    "title": "Identifying Risk Factors Earlier",
                    "desc": "The profile can show early warning signs of health conditions that are frequently linked to obesity, including diabetes, cardiovascular disease or hormonal disorders.",
                    "icon": "fa-solid fa-search"
                },
                {
                    "title": "Improved Metabolic Health",
                    "desc": "Understanding your metabolism and how your body processes the food you eat can help optimize your diet and exercise plan for successful weight loss.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "Better Nutritional Guidance",
                    "desc": "Supplementation of such deficiencies with specific vitamins enhances energy and improves overall health.",
                    "icon": "fa-solid fa-apple-alt"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Obesity Profile is perfect for anyone with excess weight, whether someone who has failed traditional weight loss approaches or someone living with symptoms of metabolic or hormonal imbalance.",
            "items": [
                {
                    "title": "Adults with a BMI above 30",
                    "desc": "Obese individuals looking for a thorough health assessment.",
                    "icon": "fa-solid fa-user"
                },
                {
                    "title": "Individuals with inexplicable weight gain",
                    "desc": "People who are struggling to lose weight despite modifying their lifestyle.",
                    "icon": "fa-solid fa-question"
                },
                {
                    "title": "Family history of obesity-related disease",
                    "desc": "People at risk of developing diseases like diabetes, hypertension, or cardiovascular disorders.",
                    "icon": "fa-solid fa-dna"
                },
                {
                    "title": "Patients who have trouble losing weight",
                    "desc": "Those who have tried diets and exercise plans and found little or no improvement.",
                    "icon": "fa-solid fa-redo"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "It is essential to go through the right preparations to be well prepared for an Obesity Profile test to get accurate results.",
            "items": [
                {
                    "title": "Fasting",
                    "desc": "Diverse blood investigations, especially those assessing glucose and lipids levels, need fasting for 8 to 12 hours before the test.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "Medication Disclosure",
                    "desc": "You should inform your healthcare provider about any medications or supplements you are taking, because some may affect the results.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Avoid Strenuous Exercise",
                    "desc": "Avoid strenuous activity for 24 hours prior to testing to reduce chances that certain hormonal or metabolic tests may be affected.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "Hydration",
                    "desc": "Drink plenty of water so blood tests are more easily carried out and help to make them accurate.",
                    "icon": "fa-solid fa-tint"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Your Obesity Profile provides specific insights into the underlying causes of obesity and includes actionable insights to help you determine appropriate treatment.",
            "items": [
                {
                    "title": "Metabolic Health",
                    "desc": "The results will clarify whether metabolic disorders like insulin resistance or thyroid dysfunction are at the root of your weight problems.",
                    "icon": "fa-solid fa-fire"
                },
                {
                    "title": "Nutritional Deficiencies",
                    "desc": "It identifies key vitamin and mineral deficiency that are possibly preventing weight loss or boosting energy.",
                    "icon": "fa-solid fa-vitamins"
                },
                {
                    "title": "Hormonal Imbalances",
                    "desc": "Identifies abnormalities in hormones like cortisol, leptin or thyroid hormones, that can directly influence control of weight.",
                    "icon": "fa-solid fa-balance-scale"
                }
            ]
        },
        "aftercareTips": {
            "heading": "How to Find Your Obesity Profile with Elite Body Home?",
            "subheading": "",
            "para": "Elite Body Home Obesity profile Home > Obesity profile Take your step at the Obesity profile At Elite Body Home, the obesity profile is much more than your weighing. With insights on your body composition, metabolic health and dietary habits, we dig deep to dive into your unique health profile. We bring together expert doctors, nutritionists, and fitness specialists who work in synergy to provide personalized recommendations based on your goals. We do this using sophisticated diagnostics tools and lab tests to find the root cause of the cause of your weight gain and your long-term path to health and well-being.",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long does the obesity profile testing take?",
                    "answer": "The complete assessment typically takes 2-3 hours including consultations and tests, with lab results available within 3-5 business days."
                },
                {
                    "question": "Will this test diagnose why I can't lose weight?",
                    "answer": "Yes, the profile is specifically designed to identify metabolic, hormonal and nutritional factors that may be contributing to weight loss resistance."
                },
                {
                    "question": "Do I need to stop medications before testing?",
                    "answer": "Some medications may need to be paused before testing - your doctor will provide specific instructions during your consultation."
                },
                {
                    "question": "How often should I repeat the obesity profile?",
                    "answer": "We recommend repeating the profile every 6-12 months to monitor progress and adjust treatment plans as needed."
                }
            ]
        }
    },
    "ar": {
        "url": "obesity-profile",
        "meta": {
            "title": "الفحص الطبي للسمنة | ايليت بودي هوم",
            "description": "تقييم تشخيصي شامل للسمنة يشمل تحاليل الدم والهرمونات والتمثيل الغذائي لتحديد أسباب زيادة الوزن ووضع خطة علاجية مخصصة",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/ar/services/obesity-profile",
            "og": {
                "title": "الفحص الطبي للسمنة | ايليت بودي هوم",
                "description": "فحص شامل لتقييم المؤشرات الصحية المرتبطة بالسمنة واضطرابات التمثيل الغذائي",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-53.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "الفحص الطبي للسمنة"],
        "hero": {
            "title": "الفحص الطبي للسمنة",
            "subtitle": "إدارة للوزن مخصصة لحالتك",
            "description": "تُعد السمنة من المشكلات الصحية الخطيرة التي تؤثر على ملايين الأشخاص حول العالم، وتؤدي إلى مضاعفات جسدية وأيضية متعددة. يجب فهم العوامل الكامنة وراء زيادة الوزن والمخاطر الصحية المرتبطة بها للتمكن من إدارة السمنة وعلاجها بفاعلية. الفحص الطبي للسمنة هو أداة تشخيصية متخصصة تهدف إلى تقييم مجموعة من المؤشرات الصحية التي قد تؤثر على وزن الجسم، والتمثيل الغذائي، والصحة العامة."
        },
        "howItWorks": {
            "title": "ما الذي يتضمنه الفحص الطبي للسمنة لدينا؟",
            "description": "يساعد هذا التقييم الشامل في كشف اضطرابات التمثيل الغذائي المحتملة، ونقص العناصر الغذائية، والعوامل المسببة للسمنة، مما يتيح لمقدمي الرعاية الصحية تصميم خطط علاجية مخصصة لكل حالة.",
            "items": [
                "تحليل السكر الصائم، 82947",
                "تحليل قياس السكر بعد الأكل، 82947",
                "تحليل صورة دم كاملة، 85025",
                "تحليل الهيموغلوبين السكري، 83036",
                "تحليل الدهون الكامل، 0",
                "تحليل كرياتين كيناز، 82550",
                "تحليل اليوريا في الدم، 84520",
                "تحليل حمض اليوريك، 84550",
                "تحليل الكرياتينين، 82565",
                "تحليل الصوديوم في الدم، 84295",
                "تحليل البوتاسيوم في الدم، 84132",
                "تحليل الكلوريد في الدم، 82435",
                "تحليل البروتين الكلي في الدم، 84155",
                "تحليل الألبومين، 82040",
                "تحليل الجلوبيولين",
                "تحليل البيليروبين الكلي، 82247",
                "تحليل البيليروبين المباشر (المقترن)، 82248",
                "تحليل الكالسيوم، 82310",
                "تحليل الفوسفات، 84100",
                "تحليل الفوسفاتاز القلوي، 84075",
                "تحليل إنزيم ناقلة أمين الألانين، 84460",
                "تحليل إنزيم ناقلة أمين الاسبارتات، 84450",
                "تحليل إنزيم ناقل الغاما غلوتاميل، 82977",
                "تحليل الغدة الدرقية، 84443",
                "تحليل مستوى ثلاثي يودوثيرونين، 84481",
                "تحليل الثيروكسين الحر، 84439",
                "تحليل العامل الروماتويدي الكمي، 86431",
                "تحليل الإنسولين، 83525",
                "تحليل الكورتيزول الصباحي، 82533",
                "تحليل البيبتيد سي العشوائي، 84681"
            ]
        },
        "procedureSteps": {
            "heading": "الإجراء",
            "para": "يُعد الفحص الطبي للسمنة مجموعة من الفحوصات المخبرية والتقييمات السريرية التي تعكس الحالة العامة للمريض وعوامل الخطر المرتبطة بالسمنة. تشمل هذه الفحوصات اختبارات الهرمونات، واختبارات التمثيل الغذائي، وغيرها من الفحوصات التي يمكن أن تكشف عن مشكلات كامنة قد تساهم في زيادة الوزن أو مقاومة فقدانه.",
            "steps": [
                {
                    "title": "استشارة أولية",
                    "desc": "يتم خلالها مناقشة مفصلة مع الطبيب حول التاريخ الطبي، ونمط الحياة، والمشكلات المتعلقة بالوزن لوضع خطة فحص مناسبة.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "تحليل مكونات الجسم",
                    "desc": "تقاس بعض المؤشرات مثل مؤشر كتلة الجسم (BMI)، ونسبة الدهون، والكتلة العضلية، وبعض المؤشرات الأخرى لفهم توزيع وزن الجسم.",
                    "icon": "fa-solid fa-weight"
                },
                {
                    "title": "الفحوصات الطبية",
                    "desc": "تُجرى فحوصات الدم للكشف عن مستويات الكوليسترول، والسكر في الدم، ووظائف الغدة الدرقية، ومؤشرات أخرى مرتبطة بالسمنة.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "تقييم عملية التمثيل الغذائي",
                    "desc": "تقييم معدل الأيض ونسبة حرق السعرات الحرارية لفهم كفاءة الجسم في استهلاك الطاقة.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "التقييم الغذائي",
                    "desc": "يتم مراجعة النظام الغذائي والعادات الغذائية بالتعاون مع أخصائي التغذية لتحديد النواقص أو العادات غير الصحية.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "تقييم النشاط البدني",
                    "desc": "يتم مراجعة مستوى النشاط الرياضي والبدني لتحديد تأثيره على الوزن والصحة العامة.",
                    "icon": "fa-solid fa-running"
                },
                {
                    "title": "مراجعة الطبيب والتوصيات",
                    "desc": "بعد جمع النتائج، يراجع الطبيب البيانات بالكامل ويقدم خطة مخصصة لإدارة الوزن تشمل النظام الغذائي، والتمارين الرياضية، وتعديلات نمط الحياة.",
                    "icon": "fa-solid fa-user-md"
                },
                {
                    "title": "خطة المتابعة",
                    "desc": "تتضمن متابعة دورية لتقييم التقدم وإجراء أي تعديلات على الخطة لضمان نجاح مستدام على المدى الطويل.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "",
            "para": "يوفر تحليل السمنة الشامل فوائد عديدة للأشخاص الذين يعانون من صعوبة في التحكم بالوزن، حيث يمنحهم فهماً دقيقاً لاحتياجاتهم الصحية الفردية. ومن أبرز الفوائد:",
            "items": [
                {
                    "title": "خطط علاجية مخصصة",
                    "desc": "يمكن للطبيب تصميم استراتيجيات مخصصة تساعد على فقدان الوزن وتحسين الصحة العامة، من خلال تحديد الأسباب الجذرية للسمنة.",
                    "icon": "fa-solid fa-clipboard-list"
                },
                {
                    "title": "الكشف المبكر عن عوامل الخطر",
                    "desc": "يساعد الملف في التعرف على مؤشرات مبكرة لأمراض شائعة مرتبطة بالسمنة مثل مرض السكري، وأمراض القلب، أو الاضطرابات الهرمونية.",
                    "icon": "fa-solid fa-search"
                },
                {
                    "title": "تحسين الصحة الأيضية",
                    "desc": "يُساهم فهم معدل التمثيل الغذائي وآلية حرق السعرات في الجسم في وضع خطط غذائية ورياضية أكثر فاعلية لإنقاص الوزن.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "توفير توجيه غذائي أفضل",
                    "desc": "يساعد الكشف عن نقص الفيتامينات والمعادن على تعديل النظام الغذائي بشكل دقيق، مما يعزز الطاقة ويحسن الشعور العام بالصحة.",
                    "icon": "fa-solid fa-apple-alt"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "يناسب علاج السمنة الشامل لأي شخص يعاني من زيادة في الوزن، خاصةً في الحالات التي لم تُحقق نتائج فعالة بالطرق التقليدية لفقدان الوزن، أو في حال ظهور أعراض لاضطرابات أيضية أو هرمونية.",
            "items": [
                {
                    "title": "البالغون الذين لديهم مؤشر كتلة جسم أعلى من 30",
                    "desc": "يُناسب الأشخاص المصنفين ضمن فئة السمنة والذين يحتاجون إلى تقييم صحي شامل.",
                    "icon": "fa-solid fa-user"
                },
                {
                    "title": "الأشخاص الذين يعانون من زيادة غير مبررة في الوزن",
                    "desc": "يلائم من يعانوا من زيادة الوزن حتى مع الالتزام بتغييرات في نمط الحياة مثل الرياضة أو النظام الغذائي.",
                    "icon": "fa-solid fa-question"
                },
                {
                    "title": "الأشخاص الذين يجدوا صعوبة في فقدان الوزن",
                    "desc": "يُعد مناسب خاصةً لمن سبق أن اتبع الأنظمة الغذائية والرياضية دون تحقيق نتائج ملموسة.",
                    "icon": "fa-solid fa-redo"
                },
                {
                    "title": "الأشخاص الذين لديهم تاريخ عائلي لأمراض مرتبطة بالسمنة",
                    "desc": "يلائم أي شخص معرض بدرجة أعلى للإصابة بأمراض مثل مرض السكري أو ارتفاع ضغط الدم أو اضطرابات القلب والأوعية الدموية.",
                    "icon": "fa-solid fa-dna"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "يجب الالتزام ببعض التحضيرات الأساسية قبل الموعد المحدد، لضمان دقة نتائج الفحوصات.",
            "items": [
                {
                    "title": "الصيام",
                    "desc": "يجب الصيام لمدة تتراوح بين 8 إلى 12 ساعة قبل إجراء بعض الفحوصات، خاصةً تلك المتعلقة بمستويات السكر والدهون في الدم.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "إبلاغ الطبيب بالأدوية",
                    "desc": "أخبر الطبيب بجميع الأدوية أو المكملات الغذائية التي تتناولها، حيث قد تؤثر بعض العقاقير على نتائج التحاليل.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "تجنب التمارين الشاقة",
                    "desc": "امتنع عن ممارسة أي نشاط بدني مكثف لمدة 24 ساعة قبل إجراء الفحوصات، حيث قد تؤثر التمارين على بعض القراءات الهرمونية أو الأيضية.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "الترطيب المستمر",
                    "desc": "احرص على شرب كميات كافية من الماء للمساعدة في تسهيل إجراء الفحوصات وضمان دقة النتائج.",
                    "icon": "fa-solid fa-tint"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "توفر النتائج رؤية شاملة لأسباب السمنة وتساعد في توجيه خطة العلاج بشكل دقيق وفعال. إليك ما يمكنك توقعه من النتائج:",
            "items": [
                {
                    "title": "الصحة الأيضية",
                    "desc": "تكشف النتائج عن وجود اضطرابات أيضية مثل مقاومة الإنسولين أو خلل في وظائف الغدة الدرقية، والتي قد تكون سببًا رئيسيًا في زيادة الوزن.",
                    "icon": "fa-solid fa-fire"
                },
                {
                    "title": "نقص العناصر الغذائية",
                    "desc": "تُظهر الفحوصات ما إذا كان هناك نقص في الفيتامينات أو المعادن الأساسية، مما قد يعيق فقدان الوزن أو يؤثر على مستويات الطاقة.",
                    "icon": "fa-solid fa-vitamins"
                },
                {
                    "title": "الاختلالات الهرمونية",
                    "desc": "تساعد في الكشف عن الاضطرابات الهرمونية مثل الكورتيزول، أو اللبتين، أو هرمونات الغدة الدرقية، والتي تلعب دورًا مباشرًا في تنظيم الوزن.",
                    "icon": "fa-solid fa-balance-scale"
                }
            ]
        },
        "aftercareTips": {
            "heading": "لماذا يجب اختيار عيادات ايليت بودي هوم؟",
            "subheading": "",
            "para": "ندمج في ايليت بودي هوم بين الخبرة ونهج علاجي يضع المريض أولوية، لضمان حصولك على أفضل رعاية ممكنة. يتمتع أطباؤنا بمهارات عالية في حقن البوتوكس، مما يضمن السلامة والدقة والنتائج المثلى.",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة المكررة",
            "subheading": "",
            "items": [
                {
                    "question": "كم تستغرق مدة الفحص الطبي للسمنة؟",
                    "answer": "يستغرق التقييم الكامل عادةً من 2-3 ساعات بما في ذلك الاستشارات والفحوصات، مع ظهور نتائج المختبر خلال 3-5 أيام عمل."
                },
                {
                    "question": "هل يكشف هذا الفحص عن سبب عدم قدرتي على فقدان الوزن؟",
                    "answer": "نعم، صُمم الفحص خصيصًا للكشف عن العوامل الأيضية والهرمونية والتغذوية التي قد تساهم في مقاومة فقدان الوزن."
                },
                {
                    "question": "هل أحتاج إلى التوقف عن تناول الأدوية قبل الفحص؟",
                    "answer": "قد تحتاج إلى التوقف عن بعض الأدوية قبل الفحص - سيقدم لك الطبيب تعليمات محددة خلال الاستشارة."
                },
                {
                    "question": "كم مرة يجب أن أعيد الفحص الطبي للسمنة؟",
                    "answer": "نوصي بإعادة الفحص كل 6-12 شهرًا لمتابعة التقدم وإجراء تعديلات على خطط العلاج حسب الحاجة."
                }
            ]
        }
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Obesity-Profile-Personalized-Weight-Management-scaled.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 46,
            "title": "Obesity Profile Diagnostic Assessment",
            "description": "Comprehensive evaluation of metabolic health and obesity-related risk factors",
            "price": 1800
        }
    }
}